<template>
  <div class="public-invoice">
    <div v-if="loading" class="pi-state">
      {{ t('invoice_loading') }}
    </div>
    <div v-else-if="error" class="pi-state pi-error">
      {{ t('invoice_notFound') }}
    </div>
    <div v-else class="pi-card">
      <div class="pi-header">
        <div>
          <div class="pi-title">{{ t('invoice_title') }} {{ invoice.invoiceNumber }}</div>
          <div class="pi-issued">{{ t('invoice_issued') }} {{ formatDate(invoice.issued_at) }}</div>
        </div>
        <BadgePill v-if="invoice.status" :variant="statusVariant" pill>
          {{ t(`invoice_status_${invoice.status}`) }}
        </BadgePill>
      </div>

      <div class="pi-parties">
        <div v-if="fromLines.length" class="pi-party">
          <div class="pi-party-label">
            {{ t('invoice_from') }}
          </div>
          <div v-for="(line, i) in fromLines" :key="i">
            {{ line }}
          </div>
        </div>
        <div v-if="billLines.length" class="pi-party">
          <div class="pi-party-label">
            {{ t('invoice_billTo') }}
          </div>
          <div v-for="(line, i) in billLines" :key="i">
            {{ line }}
          </div>
        </div>
      </div>

      <table v-if="lineItems.length" class="pi-table">
        <thead>
          <tr>
            <th>{{ t('invoice_colDescription') }}</th>
            <th>{{ t('invoice_colHours') }}</th>
            <th>{{ t('invoice_colRate') }}</th>
            <th>{{ t('invoice_colAmount') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(li, i) in lineItems" :key="i">
            <td>{{ li.description || t('invoice_colDescription') }}</td>
            <td>{{ formatHours(li.hours) }}</td>
            <td>{{ formatCurrency(li.rate) }}</td>
            <td>{{ formatCurrency((li.hours ?? 0) * (li.rate ?? 0)) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="pi-totals">
        <div v-if="invoice.tax_rate" class="pi-total-row">
          <span>{{ t('invoice_subtotal') }}</span>
          <span>{{ formatCurrency(subtotal) }}</span>
        </div>
        <div v-if="invoice.tax_rate" class="pi-total-row">
          <span>{{ t('invoice_tax') }} ({{ (invoice.tax_rate * 100).toFixed(1) }}%)</span>
          <span>{{ formatCurrency(subtotal * invoice.tax_rate) }}</span>
        </div>
        <div class="pi-total-row pi-total-due">
          <span>{{ t('invoice_amountDue') }}</span>
          <span>{{ formatCurrency(invoice.amount_due) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import BadgePill from '@/ui/components/BadgePill.vue';
import { invoicingApi } from '@/api/index.js';

const { t } = useI18n();
const route = useRoute();

const loading = ref(true);
const error = ref(false);
const invoice = ref<{
  amount_due?: number;
  client_address?: string;
  client_name?: string;
  from_address?: string;
  from_name?: string;
  invoiceNumber?: string;
  issued_at?: string;
  line_items?: Array<{ description?: string; hours: number; rate: number }>;
  status?: string;
  tax_rate?: number;
}>({});

const lineItems = computed(() =>
  Array.isArray(invoice.value.line_items) ? invoice.value.line_items : [],
);
const subtotal = computed(() =>
  lineItems.value.reduce(
    (s: number, li: { hours?: number; rate?: number }) => s + (li.hours ?? 0) * (li.rate ?? 0),
    0,
  ),
);

const fromLines = computed(() =>
  [invoice.value.from_name, invoice.value.from_address].filter(Boolean),
);
const billLines = computed(() =>
  [invoice.value.client_name, invoice.value.client_address].filter(Boolean),
);

const statusVariant = computed(() => (invoice.value.status === 'viewed' ? 'success' : 'accent'));

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString();
}
function formatCurrency(n: number | undefined): string {
  return `$${(n ?? 0).toFixed(2)}`;
}
function formatHours(n: number | undefined): string {
  return (n ?? 0).toFixed(2);
}

onMounted(async () => {
  const invoiceId = route.params.id as string;
  try {
    invoice.value = await invoicingApi.fetchPublicInvoice(invoiceId);
    // Fire-and-forget open-tracking ping -- functionally equivalent to an email pixel, but more
    // reliable here since it fires when the actual invoice page loads rather than depending on
    // an email client auto-loading a remote image (many block that by default).
    invoicingApi.pingInvoiceView(invoiceId).catch(() => {});
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.public-invoice {
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  padding: 32px 16px;
  background: var(--surface-raised);
}
.pi-state {
  padding: 48px;
  color: var(--muted);
  font-size: var(--text-sm);
}
.pi-error {
  color: var(--clr-danger);
}
.pi-card {
  width: 100%;
  max-width: 640px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 28px;
}
.pi-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
.pi-title {
  font-size: 1.25rem;
  font-weight: 700;
}
.pi-issued {
  font-size: var(--text-sm);
  color: var(--muted);
  margin-top: 2px;
}
.pi-parties {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  font-size: var(--text-sm);
}
.pi-party-label {
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
  margin-bottom: 4px;
}
.pi-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  font-size: var(--text-sm);
}
.pi-table th {
  text-align: left;
  border-bottom: 1px solid var(--border);
  padding: 6px 4px;
  color: var(--muted);
  font-weight: 600;
}
.pi-table td {
  padding: 6px 4px;
  border-bottom: 1px solid var(--border);
}
.pi-table th:not(:first-child),
.pi-table td:not(:first-child) {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.pi-totals {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}
.pi-total-row {
  display: flex;
  gap: 24px;
  font-size: var(--text-sm);
}
.pi-total-due {
  font-size: 1rem;
  font-weight: 700;
  margin-top: 6px;
}
</style>
